import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {getSalesTargetDisplayContentConfig} from '@/utils/salesTargetDisplayContentConfig';

// Base field definitions
const FIELD_DEFINITIONS = {
  title: {key: 'title', type: 'input', required: true, configKey: 'title'},
  category: {
    key: 'category',
    type: 'input',
    required: false,
    configKey: 'category'
  },
  excerpt: {
    key: 'excerpt',
    type: 'textarea',
    required: true,
    configKey: 'excerpt'
  },
  excerptInput: {
    key: 'excerpt',
    type: 'input',
    required: true,
    configKey: 'excerpt'
  },
  terms: {key: 'terms', type: 'textarea', required: true, configKey: 'terms'},
  pointsEarned: {
    key: 'pointsEarned',
    type: 'input',
    required: true,
    configKey: 'pointsEarned'
  },
  parentCategory: {
    key: 'parentCategory',
    type: 'input',
    required: false,
    configKey: 'parentCategory'
  },
  childCategory: {
    key: 'childCategory',
    type: 'input',
    required: false,
    configKey: 'childCategory'
  },
  description: {
    key: 'description',
    type: 'textarea',
    required: true,
    configKey: 'description'
  },
  titleEarlyPayment: {
    key: 'title',
    type: 'input',
    required: true,
    configKey: 'titleEarlyPayment'
  },
  termsEarlyPayment: {
    key: 'terms',
    type: 'textarea',
    required: false,
    configKey: 'termsEarlyPayment'
  },
  excerptLayered: {
    key: 'excerpt',
    type: 'textarea',
    required: false,
    configKey: 'excerptLayered'
  },
  termsLayered: {
    key: 'terms',
    type: 'textarea',
    required: false,
    configKey: 'termsLayered'
  },
  excerptLayeredV2: {
    key: 'excerpt',
    type: 'textarea',
    required: true,
    configKey: 'excerptLayeredV2'
  },
  termsLayeredV2: {
    key: 'terms',
    type: 'textarea',
    required: true,
    configKey: 'termsLayeredV2'
  },
  titlePrerequisiteV2: {
    key: 'title',
    type: 'input',
    required: true,
    configKey: 'titlePrerequisiteV2'
  },
  titleProductGroupQuantity: {
    key: 'title',
    type: 'input',
    required: true,
    configKey: 'titleProductGroupQuantity'
  },
  descriptionProductGroupQuantity: {
    key: 'description',
    type: 'textarea',
    required: true,
    configKey: 'descriptionProductGroupQuantity'
  },
  termsProductGroupQuantity: {
    key: 'terms',
    type: 'textarea',
    required: true,
    configKey: 'termsProductGroupQuantity'
  },
  titleMainTarget: {
    key: 'title',
    type: 'input',
    required: true,
    configKey: 'titleMainTarget'
  },
  categoryMainTarget: {
    key: 'category',
    type: 'input',
    required: false,
    configKey: 'categoryMainTarget'
  },
  excerptMainTarget: {
    key: 'excerpt',
    type: 'textarea',
    required: true,
    configKey: 'excerptMainTarget'
  },
  termsMainTarget: {
    key: 'terms',
    type: 'textarea',
    required: true,
    configKey: 'termsMainTarget'
  }
};

// Layout templates
const LAYOUT_TEMPLATES = {
  main: {
    inputRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2',
        fields: ['title', 'category']
      }
    ],
    textareaRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 md:gap-y-6',
        fields: ['excerpt', 'terms']
      }
    ]
  },
  layered: {
    inputRows: [
      {
        className: 'mb-4 grid grid-cols-4 gap-x-10',
        fields: ['title', 'pointsEarned', 'parentCategory', 'childCategory']
      }
    ],
    textareaRows: [
      {
        className: 'display-content-textarea gap-x-10',
        fields: ['excerpt', 'terms']
      }
    ]
  },
  prerequisite: {
    inputRows: [
      {
        className: 'grid gap-y-4',
        fields: ['title', 'category']
      }
    ],
    textareaRows: [
      {
        className: 'display-content-textarea grid gap-y-4',
        fields: ['excerpt', 'terms']
      }
    ]
  },
  earlyPayment: {
    inputRows: [
      {
        className: 'mb-6 grid grid-cols-1 gap-x-10',
        dataTestId: 'title-row',
        fields: ['title']
      }
    ],
    textareaRows: [
      {
        className:
          'display-content-textarea grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 md:gap-y-6',
        dataTestId: 'textarea-wrapper',
        fields: ['description', 'terms']
      }
    ]
  }
};

// Configuration builder
const buildConfig = (variant, fieldKeys, overrides = {}) => {
  const fields = fieldKeys.map((key) => FIELD_DEFINITIONS[key]);
  const layout = JSON.parse(JSON.stringify(LAYOUT_TEMPLATES[variant]));

  // Override field keys in layout if provided
  if (overrides.inputRowFields) {
    layout.inputRows[0].fields = overrides.inputRowFields;
  }
  if (overrides.textareaRowFields) {
    layout.textareaRows[0].fields = overrides.textareaRowFields;
  }

  return {
    variant,
    fields,
    layout,
    ...overrides.extra
  };
};

// Minimal configuration mapping
export const SALES_TARGET_DISPLAY_FIELDS_CONFIG = {
  [TARGET_TYPES.MainTarget]: buildConfig('main', [
    'titleMainTarget',
    'categoryMainTarget',
    'excerptMainTarget',
    'termsMainTarget'
  ]),
  [TARGET_TYPES.ProductTarget]: buildConfig('main', [
    'title',
    'category',
    'excerpt',
    'terms'
  ]),
  [TARGET_TYPES.MonthlyTargetLayered]: buildConfig('layered', [
    'title',
    'pointsEarned',
    'parentCategory',
    'childCategory',
    'excerptLayered',
    'termsLayered'
  ]),
  [TARGET_TYPES.ProductTargetLayered]: buildConfig('layered', [
    'title',
    'pointsEarned',
    'parentCategory',
    'childCategory',
    'excerptLayered',
    'termsLayered'
  ]),
  [TARGET_TYPES.MonthlyTargetPrerequistite]: buildConfig(
    'prerequisite',
    ['title', 'category', 'excerptInput', 'terms'],
    {extra: {hasParentChildTabs: true}}
  ),
  [TARGET_TYPES.ProductTargetPrerequisite]: buildConfig(
    'prerequisite',
    ['title', 'category', 'excerptInput', 'terms'],
    {extra: {hasParentChildTabs: true}}
  ),
  [TARGET_TYPES.EarlyPaymentTarget]: buildConfig('earlyPayment', [
    'titleEarlyPayment',
    'description',
    'termsEarlyPayment'
  ]),
  [TARGET_TYPES.DropSizeTarget]: buildConfig('earlyPayment', [
    'titleEarlyPayment',
    'description',
    'termsEarlyPayment'
  ]),
  [TARGET_TYPES.ProductGroupQuantity]: buildConfig(
    'main',
    [
      'titleProductGroupQuantity',
      'descriptionProductGroupQuantity',
      'termsProductGroupQuantity'
    ],
    {
      inputRowFields: ['title'],
      textareaRowFields: ['description', 'terms']
    }
  )
};

export const SALES_TARGET_DISPLAY_FIELDS_CONFIG_V2_OVERRIDES = {
  [TARGET_TYPES.MonthlyTargetLayered]: buildConfig('layered', [
    'title',
    'pointsEarned',
    'parentCategory',
    'childCategory',
    'excerptLayeredV2',
    'termsLayeredV2'
  ]),
  [TARGET_TYPES.ProductTargetLayered]: buildConfig('layered', [
    'title',
    'pointsEarned',
    'parentCategory',
    'childCategory',
    'excerptLayeredV2',
    'termsLayeredV2'
  ]),
  [TARGET_TYPES.MonthlyTargetPrerequistite]: buildConfig(
    'prerequisite',
    ['titlePrerequisiteV2', 'category', 'excerptInput', 'terms'],
    {extra: {hasParentChildTabs: true}}
  ),
  [TARGET_TYPES.ProductTargetPrerequisite]: buildConfig(
    'prerequisite',
    ['titlePrerequisiteV2', 'category', 'excerptInput', 'terms'],
    {extra: {hasParentChildTabs: true}}
  )
};

export const getDisplayFieldsConfig = (
  targetType,
  version = getSalesTargetDisplayContentConfig(targetType).version
) => {
  if (version === 'v2') {
    return (
      SALES_TARGET_DISPLAY_FIELDS_CONFIG_V2_OVERRIDES[targetType] ??
      SALES_TARGET_DISPLAY_FIELDS_CONFIG[targetType]
    );
  }
  return SALES_TARGET_DISPLAY_FIELDS_CONFIG[targetType];
};

export const getRequiredDisplayFieldKeys = (targetType, version) => {
  const config = getDisplayFieldsConfig(targetType, version);
  if (!config) return null;
  return [
    ...new Set(config.fields.filter((f) => f.required).map((f) => f.key))
  ];
};
